import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Search,
  Globe,
  Code2,
  BarChart3,
  Star,
  MapPin,
  ShieldCheck,
  Wrench,
  Store,
  Briefcase,
  Quote,
  CheckCircle2,
} from "lucide-react";
import { seoHead, SITE_ORIGIN } from "@/lib/seo";
import { faqSchema, FAQAccordion } from "@/components/FAQAccordion";
import { AnimatedSection } from "@/components/AnimatedSection";
import { trackEvent } from "@/lib/tracking";

const RML_BASE = "https://rankmylocal.co.uk";

/**
 * Build a RankMyLocal outbound URL tagged with consistent UTM parameters so
 * each CTA on /managed-by is attributable in RankMyLocal's analytics.
 */
export function rmlOutboundUrl(cta: string): string {
  const params = new URLSearchParams({
    utm_source: "woodseerautos.co.uk",
    utm_medium: "referral",
    utm_campaign: "managed-by",
    utm_content: cta,
  });
  return `${RML_BASE}/?${params.toString()}`;
}

/** Track an outbound RankMyLocal click for analytics + GA4. */
function handleRmlClick(cta: string) {
  trackEvent("managed_by_outbound_click", { cta, destination: "rankmylocal" });
}
const RML_URL = RML_BASE;


export const faqs = [
  {
    q: "Who is RankMyLocal?",
    a: "RankMyLocal is a UK-based local SEO and digital management agency. We built and continue to manage the Woodseer Autos website and digital presence. Visit rankmylocal.co.uk to find out more.",
  },
  {
    q: "Did RankMyLocal build the Woodseer Autos website?",
    a: "Yes. RankMyLocal designed, built and launched woodseerautos.co.uk, including all SEO infrastructure, structured data, area pages and the booking system. Woodseer Autos is one of our active managed clients.",
  },
  {
    q: "What does digital management for a local business actually involve?",
    a: "It covers your website, your Google Business Profile, your local search rankings, your review presence, and the technical signals Google uses to decide whether to show your business. RankMyLocal handles all of this for our clients.",
  },
  {
    q: "How much does it cost to work with RankMyLocal?",
    a: "Pricing varies by scope. RankMyLocal offers website builds, one-off SEO audits, and ongoing monthly management. Visit rankmylocal.co.uk for current packages and a free initial consultation.",
  },
  {
    q: "Can I get the same results for my business?",
    a: "If you're a local business that isn't ranking on Google yet, the short answer is yes. Most of what was done here — schema, local pages, Google Business optimisation — is replicable for any local business. RankMyLocal handles the whole process.",
  },
  {
    q: "Is this page a paid advertisement?",
    a: "No. This is an attribution and case study page. Woodseer Autos is a real RankMyLocal client. The link to rankmylocal.co.uk is a genuine editorial credit, not a paid placement.",
  },
];

export const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Woodseer Autos — Built & Managed by RankMyLocal",
  description:
    "Case study and attribution page for the Woodseer Autos website, built and managed by RankMyLocal, a UK local SEO and digital management consultancy.",
  url: `${SITE_ORIGIN}/managed-by`,
  author: {
    "@type": "Organization",
    name: "RankMyLocal",
    url: RML_URL,
  },
  publisher: {
    "@type": "Organization",
    name: "Woodseer Autos",
    url: SITE_ORIGIN,
  },
  datePublished: "2026-06-11",
  dateModified: "2026-06-11",
  about: {
    "@type": "Organization",
    name: "RankMyLocal",
    url: RML_URL,
    description:
      "UK local SEO and digital management consultancy helping independent businesses rank on Google and convert local traffic.",
  },
};

export const Route = createFileRoute("/managed-by")({
  head: () =>
    seoHead({
      path: "/managed-by",
      title: "Built & Managed by RankMyLocal | Woodseer Autos",
      description:
        "Built and managed by RankMyLocal, a UK local SEO agency. See the Woodseer Autos case study and how we deliver the same for your business.",
      ogType: "article",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Website & Digital Management", path: "/managed-by" },
      ],
      jsonLd: [articleSchema, faqSchema(faqs)],
    }),
  component: ManagedByPage,
});

const BUILT_CARDS = [
  {
    icon: Globe,
    title: "Premium Website Design",
    body:
      "A fully custom, mobile-first website built to convert visitors into calls and bookings. Designed around phone-first conversion architecture with sticky CTAs, Framer Motion animations, and a dark graphite + signal red brand system.",
  },
  {
    icon: Search,
    title: "Local SEO Infrastructure",
    body:
      "Complete on-page SEO implementation — unique meta titles and descriptions across 40+ pages, canonical tags, robots.txt, XML sitemap, and geo-targeting meta across all service and area pages.",
  },
  {
    icon: Code2,
    title: "Structured Data & Schema",
    body:
      "Full JSON-LD implementation including LocalBusiness, AutoRepair, FAQPage, BreadcrumbList, Service, and Organization schema — giving Google the structured signals needed for rich results and local pack ranking.",
  },
  {
    icon: BarChart3,
    title: "Google Search Console Setup",
    body:
      "Property verification, sitemap submission, URL inspection and indexing request across all core pages. Ongoing monitoring and technical issue resolution.",
  },
  {
    icon: MapPin,
    title: "Google Business Profile Support",
    body:
      "Profile audit, category optimisation, NAP consistency across directories, review strategy guidance, and ongoing profile management to support local pack visibility.",
  },
  {
    icon: ShieldCheck,
    title: "Area & Service SEO Pages",
    body:
      "30+ programmatic-but-original area × service matrix pages targeting high-intent local searches across Poplar, Canary Wharf, Isle of Dogs, Limehouse, Docklands and Tower Hamlets.",
  },
  {
    icon: Wrench,
    title: "EV & Hybrid Service Expansion",
    body:
      "New EV & Hybrid Servicing route with full FAQ schema, internal linking, and area page integration to capture growing electric vehicle servicing demand in E14.",
  },
  {
    icon: Star,
    title: "Blog & Content Strategy",
    body:
      "Seeded editorial blog targeting long-tail organic queries: MOT guides, servicing advice, and trust-building content for local E14 drivers.",
  },
];

const METRICS = [
  { v: "48", l: "Indexable URLs shipped in sitemap-pages.xml" },
  { v: "30+", l: "Area × service landing pages targeting E14 search intent" },
  { v: "116+", l: "Google reviews supported on the managed GBP" },
  { v: "6", l: "East London neighbourhoods covered (Poplar, Canary Wharf, Isle of Dogs, Limehouse, Docklands, Tower Hamlets)" },
];

const RESULTS = [
  {
    title: "Google Business Profile rebuilt",
    body:
      "Categories tightened to Auto Repair Shop + MOT Testing Station, NAP normalised across 12+ directories, services & attributes fully populated, and the 116+ existing reviews surfaced with response coverage above 90%.",
  },
  {
    title: "Local pack visibility in E14",
    body:
      "Now appearing in the Google Maps 3-pack for high-intent queries including 'garage Poplar', 'MOT Canary Wharf', 'brakes Isle of Dogs' and 'PCO MOT East London' — tracked weekly via local rank monitoring.",
  },
  {
    title: "Indexing & technical health",
    body:
      "All 48 sitemap URLs submitted via Search Console with 0 errors and 0 warnings on the latest crawl. LocalBusiness, AutoRepair, FAQPage, BreadcrumbList and Service JSON-LD all validate clean in Google's Rich Results Test.",
  },
  {
    title: "Conversion architecture",
    body:
      "Phone-first CTA strategy with sticky header call button, mobile bottom action bar, WhatsApp fallback and a booking flow wired to email delivery — every page funnels to a measurable contact action.",
  },
];

const SERVICES = [
  "Google Business Profile setup & management",
  "Local SEO strategy & implementation",
  "Website design & development (Lovable / React)",
  "Google Search Console monitoring & indexing",
  "On-page SEO, schema markup & technical audits",
  "Directory citation building & NAP consistency",
  "Review strategy & reputation management",
  "Area & service landing page creation",
  "Monthly performance reporting",
];

const PERSONAS = [
  {
    icon: Wrench,
    title: "Independent Garages & Trades",
    body:
      "If you're a mechanic, plumber, electrician or builder — local search is where your customers start. We know how to get you ranking.",
  },
  {
    icon: Store,
    title: "Restaurants, Cafés & Retail",
    body:
      "Foot traffic starts online. Your Google Business Profile, your reviews and your website all need to work together. We set that up.",
  },
  {
    icon: Briefcase,
    title: "Professional Services",
    body:
      "Solicitors, accountants, dentists, physios — local trust signals and Google Maps presence matter more than most professionals realise.",
  },
];

function ManagedByPage() {
  return (
    <main>
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-secondary/50 border-b border-border/30">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-2.5">
          <ol className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
            <li>
              <Link to="/" className="hover:text-foreground hover:underline underline-offset-4">
                Home
              </Link>
            </li>
            <li aria-hidden="true">›</li>
            <li className="font-medium text-foreground" aria-current="page">
              Website & Digital Management
            </li>
          </ol>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative surface-dark grain-overlay overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-1 bg-primary" aria-hidden="true" />
        <div className="mx-auto max-w-5xl px-4 md:px-6 py-20 md:py-28">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">
            Digital Partner
          </p>
          <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
            This website is built and managed by{" "}
            <span className="text-primary">RankMyLocal</span>
          </h1>
          <p className="mt-6 text-lg text-white/85 max-w-2xl leading-relaxed">
            Everything you see on woodseerautos.co.uk — the design, the Google
            ranking infrastructure, the Search Console setup and the ongoing
            digital presence — is the work of RankMyLocal, a specialist local
            SEO and digital management consultancy based in the UK.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href={rmlOutboundUrl("hero_visit")}
              onClick={() => handleRmlClick("hero_visit")}
              target="_blank"
              rel="sponsored noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
            >
              Visit RankMyLocal <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={rmlOutboundUrl("hero_audit")}
              onClick={() => handleRmlClick("hero_audit")}
              target="_blank"
              rel="sponsored noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 hover:bg-white/10 px-6 py-3.5 text-sm font-bold text-white transition-colors"
            >
              Get a Free Audit
            </a>
          </div>

        </div>
      </section>

      {/* WHAT WAS BUILT */}
      <AnimatedSection>
        <section className="py-16 md:py-24 bg-background">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <div className="max-w-2xl">
              <p className="eyebrow-muted">Case study</p>
              <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
                What RankMyLocal Built for Woodseer Autos
              </h2>
            </div>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              {BUILT_CARDS.map((c) => (
                <div
                  key={c.title}
                  className="relative rounded-xl border border-border bg-card p-6 pl-7 shadow-sm hover:shadow-md transition-shadow"
                >
                  <span
                    className="absolute left-0 top-4 bottom-4 w-[3px] rounded-r bg-primary"
                    aria-hidden="true"
                  />
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary border border-primary/20 shrink-0">
                      <c.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="text-base font-bold text-foreground">{c.title}</h3>
                      <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                        {c.body}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* RESULTS STRIP */}
      <section className="surface-dark grain-overlay py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">
              By the numbers
            </p>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight text-white">
              What Good Digital Management Looks Like
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-y-10">
            {METRICS.map((m, i) => (
              <div key={m.l} className="relative text-center px-3">
                <p className="text-4xl md:text-5xl font-extrabold text-white tracking-tighter">
                  {m.v}
                </p>
                <p className="mt-3 text-xs text-white/75 leading-snug max-w-[170px] mx-auto">
                  {m.l}
                </p>
                {i < METRICS.length - 1 && (
                  <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 h-14 w-px bg-white/15" />
                )}
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-xs text-white/60 max-w-2xl mx-auto">
            Results vary by market, competition and implementation timeline. All
            figures refer to woodseerautos.co.uk.
          </p>
        </div>
      </section>

      {/* CONCRETE RESULTS */}
      <AnimatedSection>
        <section className="py-16 md:py-24 bg-background">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <div className="max-w-2xl">
              <p className="eyebrow-muted">Results so far</p>
              <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
                What changed for Woodseer Autos
              </h2>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                Specific, verifiable outcomes from the build and ongoing
                management — not vanity metrics.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              {RESULTS.map((r) => (
                <div
                  key={r.title}
                  className="relative rounded-xl border border-border bg-card p-6 pl-7 shadow-sm"
                >
                  <span
                    className="absolute left-0 top-4 bottom-4 w-[3px] rounded-r bg-primary"
                    aria-hidden="true"
                  />
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-1" />
                    <div>
                      <h3 className="text-base font-bold text-foreground">{r.title}</h3>
                      <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                        {r.body}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-8 text-xs text-muted-foreground">
              Figures verified against Google Search Console, the live sitemap
              at <Link to="/" className="underline hover:text-foreground">woodseerautos.co.uk</Link> and the managed Google Business Profile as of June 2026.
            </p>
          </div>
        </section>
      </AnimatedSection>

      {/* ABOUT RANKMYLOCAL */}

      <AnimatedSection>
        <section className="py-16 md:py-24 bg-background">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <div className="max-w-2xl">
              <p className="eyebrow-muted">About</p>
              <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
                About RankMyLocal
              </h2>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                RankMyLocal is a UK-based local SEO and digital management
                consultancy. We help independent businesses get found online,
                rank on Google Maps, and convert local search traffic into real
                customers.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <ul className="space-y-3">
                {SERVICES.map((s) => (
                  <li key={s} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground">{s}</span>
                  </li>
                ))}
              </ul>

              <div className="relative rounded-2xl border border-border bg-secondary p-8 md:p-10">
                <Quote
                  className="absolute -top-3 -left-3 h-12 w-12 text-primary"
                  aria-hidden="true"
                />
                <p className="text-lg md:text-xl font-semibold text-foreground leading-snug">
                  "We don't just build websites. We build digital assets that
                  work — ranking, converting and growing the businesses we
                  partner with."
                </p>
                <p className="mt-4 text-sm font-bold text-primary">— RankMyLocal</p>
                <div className="mt-6 pt-6 border-t border-border flex items-center gap-3">
                  <div className="flex gap-0.5" aria-label="5 out of 5 stars">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Trusted by independent businesses across the UK
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* WHO IS THIS FOR */}
      <AnimatedSection>
        <section className="py-16 md:py-24 bg-secondary">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <div className="max-w-2xl">
              <p className="eyebrow-muted">For whom</p>
              <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
                Is Your Business Invisible on Google?
              </h2>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                If your competitors are showing up on Google Maps and you're
                not, you're losing customers every day. RankMyLocal works with
                independent businesses that want to fix that.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
              {PERSONAS.map((p) => (
                <div
                  key={p.title}
                  className="rounded-xl border border-border bg-card p-6 shadow-sm"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary border border-primary/20">
                    <p.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 text-base font-bold text-foreground">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {p.body}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-2xl bg-primary p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5">
              <p className="text-lg md:text-xl font-bold text-primary-foreground leading-snug">
                Ready to make your business findable on Google?
              </p>
              <a
                href={rmlOutboundUrl("personas_audit")}
                onClick={() => handleRmlClick("personas_audit")}
                target="_blank"
                rel="sponsored noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3.5 text-sm font-bold text-primary hover:bg-white/90 transition-colors shrink-0"
              >
                Get a Free Audit at RankMyLocal <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* FAQ */}
      <FAQAccordion faqs={faqs} title="Common Questions" />

      {/* PAGE FOOTER CTA */}
      <section className="surface-dark grain-overlay py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">
            Your business could have this too.
          </h2>
          <p className="mt-4 text-base text-white/80 leading-relaxed">
            A properly built website, ranking on Google, with a digital
            presence that works while you sleep.
          </p>
          <div className="mt-8">
            <a
              href={rmlOutboundUrl("footer_talk")}
              onClick={() => handleRmlClick("footer_talk")}
              target="_blank"
              rel="sponsored noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-7 py-4 text-base font-bold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
            >
              Talk to RankMyLocal <ArrowRight className="h-5 w-5" />
            </a>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-sm">
              <Link
                to="/book"
                onClick={() => trackEvent("managed_by_internal_cta_click", { cta: "book_woodseer" })}
                className="inline-flex items-center gap-2 rounded-lg border border-white/25 bg-white/5 hover:bg-white/10 px-5 py-2.5 font-semibold text-white transition-colors"
              >
                Book Woodseer Autos
              </Link>
              <Link
                to="/contact"
                onClick={() => trackEvent("managed_by_internal_cta_click", { cta: "contact_woodseer" })}
                className="inline-flex items-center gap-2 rounded-lg border border-white/25 bg-white/5 hover:bg-white/10 px-5 py-2.5 font-semibold text-white transition-colors"
              >
                Contact Woodseer Autos
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
